import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Checkbox,
  Divider as MuiDivider,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  List,
  ListItem,
  TextField,
  Typography as MuiTypography,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Authorization Requirements
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/documentation/policies-and-procedures">
              Policies And Procedures
            </Link>
            <Typography>Authorization Requirements</Typography>
          </Breadcrumbs>
          <Divider my={6} />
          <Typography variant="body1" paragraph>
            This document details the specific circumstances under which our SaaS medical application may use or
            disclose Protected Health Information (PHI) without an individual's explicit authorization, in compliance
            with the Health Insurance Portability and Accountability Act (HIPAA). Understanding these exceptions is
            crucial for ensuring that our practices remain within legal boundaries, while still facilitating the
            necessary flow of health information for treatment, payment, and healthcare operations, among other
            permitted or required activities. This section clarifies the scope and limitations of such disclosures,
            ensuring that all team members are aware of when and how PHI can be shared responsibly.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Authorization Exceptions
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Treatment Activities:</b> PHI may be used or disclosed without authorization for the purposes of
                  treatment activities, facilitating the coordination or management of healthcare and related services
                  by one or more healthcare providers, including the coordination or management of healthcare by a
                  healthcare provider with a third party, consultation between providers, and the referral of a patient
                  from one provider to another.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Payment Activities:</b> PHI can be disclosed without prior consent for the purposes of obtaining
                  payment for the healthcare services provided. This includes determinations of eligibility and
                  coverage, billing, claims management, collection activities, and related healthcare data processing.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Healthcare Operations:</b> Use and disclosure of PHI are permitted without individual authorization
                  for certain healthcare operations, including but not limited to quality assessment and improvement
                  activities, training programs, accreditation, certification, licensing, or credentialing activities,
                  and review or auditing of services.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Public Health and Safety:</b> PHI may be disclosed to public health authorities or other authorized
                  entities for purposes related to preventing or controlling disease, injury, or disability, reporting
                  child abuse or neglect, reporting reactions to medications or problems with products, notifying
                  individuals of recalls, and conducting post-marketing surveillance.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Compliance with Law:</b> Disclosures may be made when required by law, including disclosures to
                  comply with court orders, legal proceedings, or law enforcement requests.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Guidelines for Handling Exceptions
          </Typography>
          <List>
            <ListItem>
              All staff members must verify the circumstances of the exception to ensure compliance with HIPAA
              regulations before making any disclosure without authorization.
            </ListItem>
            <ListItem>
              Documentation of all disclosures made under these exceptions must be maintained, including the date of
              disclosure, the recipient of the information, and a brief description of the PHI disclosed.
            </ListItem>
            <ListItem>
              Staff should receive regular training on these exceptions to ensure they understand when and how PHI can
              be disclosed without violating privacy rules.
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
